// MACHINERY V2 (Stroje) — the 248px catalog filter rail (M2.8).
//
// Spec: data package §11, the "Catalog filters + prefill" field table.
// Reference impl: components/CatalogBrowsePage.tsx (its sidebar).
// Artboards 0900 / 0901 draw the rail: brand search, VÝROBCA, MIDAS READINESS,
// OTÁČKY VRETENA, VÝKON VRETENA, TAPER.
//
// >>> §9 D6 — THE FOUR PREVIOUSLY-DEAD PARAMS GET REAL INPUTS HERE. <<<
// THE DESIGNER DID NOT DRAW THEM. Artboards 0900/0901 stop at taper, because
// the params had no UI when the boards were made. D6 rules them IN and the D4
// consistency rule says a carried/added feature is EXTRAPOLATED from the
// established idiom rather than newly designed, so:
//   · minTools / maxTools  -> a RangeRow, byte-identical in shape to the RPM
//                             and HP rows the artboard DOES draw (min – max,
//                             mono inputs, one eyebrow above);
//   · operationTypeId      -> a Row list with a "Všetky" reset row, exactly
//   · controlSystemBrandId -> like the TAPER list two sections above it.
// Nothing new was invented; each control is an existing rail idiom pointed at a
// new param. `catalogQuery.ts` then counts all four, which is the other half of
// D6 ("activeFilterCount becomes accurate").
//
// >>> WHERE THE TWO NEW OPTION LISTS COME FROM — AND WHY NO BACKEND WORK. <<<
// The catalog API has no brands/operation-types endpoint of its own, but it
// does not need one: `CatalogMachineModel.OperationTypeRef` is typed
// `OperationTypes.Models.OperationType` and `.ControlSystemBrand` is
// `ControlSystemBrand` (CatalogMachineModel.cs:55 and :57), i.e. the SAME
// reference entities that `GET /v1/customer/machines/types/{id}/operation-types`
// and `.../control-system-brands` already return
// (CustomerMachinesController.cs:260-268 and :280-288, via
// `_operationTypesService` / `_controlSystemsService`). The catalog FK columns
// point at those tables (CatalogMachineModelConfiguration.cs:77-82 and :91-96),
// so an id from either endpoint is directly usable as a catalog filter value.
// The lists are therefore fetched with the EXISTING thunks the custom-machine
// form already uses, for the CNC machine type — the catalog is overwhelmingly
// machine tools (the add path no longer forces `typeId`; MACH-TYPE-DEFAULT-1).
// `useCatalogBrowse.ts` owns that fetch; this component only renders.
//
// A brand/operation-type row the current category has no models for simply
// returns zero results, exactly as an over-narrow RPM range does. The rail does
// not fake per-facet counts for these two lists: the API returns no counts for
// them and inventing one client-side would be a number we cannot stand behind
// (the same reason v1's taper list ships without counts).
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { getReferenceLabel, sortReferenceRows } from "../../../../shared/utils/machineReferenceLabels";
import { CatalogBrowseQuery, TaperType } from "../../models/models";
import { Row, Section } from "../fleet/FilterRail";
import { CatalogFacetModel } from "./catalogFacets";
import CatalogBrandFacets from "./CatalogBrandFacets";
import { RailRangeRow, RailSearch, RailToggleRow } from "./catalogRailAtoms";
import {
  parseRangeFloat,
  parseRangeInt,
  toggleCatalogFlag,
  withCatalogFilter,
} from "./catalogQuery";

export interface CatalogFilterRailProps {
  query: CatalogBrowseQuery;
  /**
   * An UPDATER, not a plain setter. Since M2.8 the range inputs publish on a
   * debounce (catalogRailAtoms.tsx `RangeInput`), so the publish can land after
   * the user has changed some OTHER filter — and a `withCatalogFilter(query,…)`
   * built from this render's `query` would then spread a stale snapshot back
   * over that change and silently undo it. Every write below therefore goes
   * through `(prev) => …` and reads the freshest state at commit time.
   */
  onQueryChange: React.Dispatch<React.SetStateAction<CatalogBrowseQuery>>;
  /** Client-side facet-list filter — never a request param (catalogFacets.ts). */
  brandSearch: string;
  onBrandSearchChange: (value: string) => void;
  facets: CatalogFacetModel;
  brandsExpanded: boolean;
  onToggleBrandsExpanded: () => void;
  expandedParents: Set<string>;
  onToggleParent: (id: string) => void;
  /** Total across the current category — the "all brands" row's count. */
  totalCount: number;
  taperTypes: TaperType[];
  operationTypes: { id: string; name: string; code?: string | null }[];
  controlSystemBrands: { id: string; name: string; code?: string | null }[];
}

const CatalogFilterRail: React.FC<CatalogFilterRailProps> = ({
  query,
  onQueryChange,
  brandSearch,
  onBrandSearchChange,
  facets,
  brandsExpanded,
  onToggleBrandsExpanded,
  expandedParents,
  onToggleParent,
  totalCount,
  taperTypes,
  operationTypes,
  controlSystemBrands,
}) => {
  const { translate } = useTranslation("myMachinery");

  const set = (patch: Partial<CatalogBrowseQuery>) =>
    onQueryChange((prev) => withCatalogFilter(prev, patch));

  return (
    <aside
      style={{
        width: 248,
        flexShrink: 0,
        padding: "16px 14px",
        background: "var(--bg-soft)",
        borderRight: "1px solid var(--border)",
        display: "flex",
        flexDirection: "column",
        gap: 16,
        overflowY: "auto",
        minHeight: 0,
      }}
    >
      <RailSearch
        value={brandSearch}
        onChange={onBrandSearchChange}
        placeholder={translate("myMachinery:searchBrands")}
        ariaLabel={translate("myMachinery:searchBrands")}
      />

      <CatalogBrandFacets
        facets={facets}
        selectedManufacturerId={query.manufacturerId}
        onSelect={(id) => set({ manufacturerId: id })}
        brandSearch={brandSearch}
        expanded={brandsExpanded}
        onToggleExpanded={onToggleBrandsExpanded}
        expandedParents={expandedParents}
        onToggleParent={onToggleParent}
        totalCount={totalCount}
      />

      {/* DR-1 / DR-2. Both toggles OMIT when unchecked and never send `false` —
          the rule lives in catalogQuery.ts `toggleCatalogFlag`. */}
      <Section title={translate("myMachinery:midasReadiness")}>
        <RailToggleRow
          label={translate("myMachinery:oemReady")}
          dot="oem"
          active={!!query.oemReady}
          onClick={() => onQueryChange((prev) => toggleCatalogFlag(prev, "oemReady"))}
        />
        <RailToggleRow
          label={translate("myMachinery:machineIdIntegrated")}
          dot="midas"
          active={!!query.machineIdIntegrated}
          onClick={() =>
            onQueryChange((prev) => toggleCatalogFlag(prev, "machineIdIntegrated"))
          }
        />
      </Section>

      <RailRangeRow
        title={translate("myMachinery:spindleRpmRange")}
        min={query.minRpm}
        max={query.maxRpm}
        onMin={(raw) => set({ minRpm: parseRangeInt(raw) })}
        onMax={(raw) => set({ maxRpm: parseRangeInt(raw) })}
      />

      {/* §11 names this pair MinHp/MaxHp and the card renders "{n} HP", so the
          label is HP. Artboards 0900/0901 letter it "VÝKON VRETENA · KW" — an
          artboard-vs-data-package conflict resolved FOR the data package (Wave
          4 precedent), because relabelling the SAME unfiltered number as kW
          would send a value the API reads as horsepower. */}
      <RailRangeRow
        title={translate("myMachinery:spindleHpRange")}
        min={query.minHp}
        max={query.maxHp}
        onMin={(raw) => set({ minHp: parseRangeFloat(raw) })}
        onMax={(raw) => set({ maxHp: parseRangeFloat(raw) })}
      />

      {/* D6 — new. Same RangeRow as the two above it. */}
      <RailRangeRow
        title={translate("myMachinery:v2.catalog.toolCountRange")}
        min={query.minTools}
        max={query.maxTools}
        onMin={(raw) => set({ minTools: parseRangeInt(raw) })}
        onMax={(raw) => set({ maxTools: parseRangeInt(raw) })}
      />

      {taperTypes.length > 0 && (
        <Section title={translate("myMachinery:taperType")}>
          <Row
            label={translate("myMachinery:allTaperTypes")}
            active={!query.taperTypeId}
            check
            onClick={() => set({ taperTypeId: undefined })}
          />
          {taperTypes.map((tt) => (
            <Row
              key={tt.id}
              label={getReferenceLabel("taper", tt, translate)}
              active={query.taperTypeId === tt.id}
              check
              onClick={() =>
                set({ taperTypeId: query.taperTypeId === tt.id ? undefined : tt.id })
              }
            />
          ))}
        </Section>
      )}

      {/* D6 — new. Same list idiom as TAPER above. */}
      {controlSystemBrands.length > 0 && (
        <Section title={translate("myMachinery:controlSystemBrand")}>
          <Row
            label={translate("myMachinery:v2.catalog.allControlBrands")}
            active={!query.controlSystemBrandId}
            check
            onClick={() => set({ controlSystemBrandId: undefined })}
          />
          {controlSystemBrands.map((b) => (
            <Row
              key={b.id}
              label={getReferenceLabel("brand", b, translate)}
              active={query.controlSystemBrandId === b.id}
              check
              onClick={() =>
                set({
                  controlSystemBrandId:
                    query.controlSystemBrandId === b.id ? undefined : b.id,
                })
              }
            />
          ))}
        </Section>
      )}

      {/* D6 — new. Same list idiom again. */}
      {operationTypes.length > 0 && (
        <Section title={translate("myMachinery:operationType")}>
          <Row
            label={translate("myMachinery:v2.catalog.allOperationTypes")}
            active={!query.operationTypeId}
            check
            onClick={() => set({ operationTypeId: undefined })}
          />
          {sortReferenceRows(operationTypes, "operation", translate).map((o) => (
            <Row
              key={o.id}
              label={getReferenceLabel("operation", o, translate)}
              active={query.operationTypeId === o.id}
              check
              onClick={() =>
                set({ operationTypeId: query.operationTypeId === o.id ? undefined : o.id })
              }
            />
          ))}
        </Section>
      )}
    </aside>
  );
};

export default CatalogFilterRail;
